import { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllRows, dashboardCount } from '@/lib/supabaseHelpers';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from 'recharts';
import { ArrowLeft, Users, Clock, Monitor, MousePointerClick, TrendingUp, BarChart3, CalendarIcon, Eye, FlaskConical, Euro } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format, subDays, isAfter, isBefore, startOfDay, endOfDay } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import StatCard from '@/components/dashboard/StatCard';
import ConversionFunnel from '@/components/dashboard/ConversionFunnel';
import LiveRawData from '@/components/dashboard/LiveRawData';
import LiveActivityFeed from '@/components/dashboard/LiveActivityFeed';
import LiveAfhaakFunnel from '@/components/dashboard/LiveAfhaakFunnel';
import LiveTimingMetrics from '@/components/dashboard/LiveTimingMetrics';
import LiveAnswerDistribution from '@/components/dashboard/LiveAnswerDistribution';
import LiveDeviceConversion from '@/components/dashboard/LiveDeviceConversion';
import LiveUTMPerformance from '@/components/dashboard/LiveUTMPerformance';
import LiveHourHeatmap from '@/components/dashboard/LiveHourHeatmap';
import LiveBounceConversionSplit from '@/components/dashboard/LiveBounceConversionSplit';
import LiveLogoClicks from '@/components/dashboard/LiveLogoClicks';
import LiveABTest from '@/components/dashboard/LiveABTest';
import ABVisualComparison from '@/components/dashboard/ABVisualComparison';
import ABTestLogbook from '@/components/dashboard/ABTestLogbook';
import ABSpendSplit from '@/components/dashboard/ABSpendSplit';
import LiveMetaAds from '@/components/dashboard/LiveMetaAds';
import LiveShopifyOrders from '@/components/dashboard/LiveShopifyOrders';
import LiveTotalOverview from '@/components/dashboard/LiveTotalOverview';
import DateFilterBar from '@/components/dashboard/DateFilterBar';

const COLORS = ['#0F1C2E', '#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

interface SheetRow {
  date: string;
  answers: Record<string, string>;
  totalDuration: string;
  deviceType: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  backClicks: number;
  scrollDepth: string;
  screenTimes: Record<string, number>;
  lastScreenIndex: number;
  completed: boolean;
}

const METRIC_HEADERS = ['Doorlooptijd', 'Apparaat', 'UTM Source', 'UTM Medium', 'UTM Campaign', 'Terug-kliks', 'Scroll diepte', 'Schermtijden', 'Laatste scherm', 'Afhaak scherm'];

function parseRows(headers: string[], rows: string[][]): SheetRow[] {
  // Find metric columns by header name for robust parsing
  const colIndex = (name: string) => headers.indexOf(name);
  const datumIdx = colIndex('Datum');
  const durationIdx = colIndex('Doorlooptijd');
  const deviceIdx = colIndex('Apparaat');
  const utmSourceIdx = colIndex('UTM Source');
  const utmMediumIdx = colIndex('UTM Medium');
  const utmCampaignIdx = colIndex('UTM Campaign');
  const backClicksIdx = colIndex('Terug-kliks');
  const scrollIdx = colIndex('Scroll diepte');
  const screenTimesIdx = colIndex('Schermtijden');
  const lastScreenIdx = colIndex('Laatste scherm');
  const completedIdx = colIndex('Voltooid');

  // Answer columns = all headers that are not metrics and not Sessie ID/Datum/Voltooid
  const skipHeaders = new Set(['Sessie ID', 'Datum', 'Voltooid', ...METRIC_HEADERS]);
  const answerHeaders = headers.filter(h => !skipHeaders.has(h));

  return rows.map(row => {
    const getVal = (idx: number) => idx >= 0 ? (row[idx] || '') : '';

    let screenTimes: Record<string, number> = {};
    try {
      const raw = getVal(screenTimesIdx);
      if (raw) screenTimes = JSON.parse(raw);
    } catch { /* ignore */ }

    return {
      date: getVal(datumIdx),
      answers: Object.fromEntries(
        answerHeaders.map(h => [h, getVal(headers.indexOf(h))])
      ),
      totalDuration: getVal(durationIdx),
      deviceType: getVal(deviceIdx),
      utmSource: getVal(utmSourceIdx),
      utmMedium: getVal(utmMediumIdx),
      utmCampaign: getVal(utmCampaignIdx),
      backClicks: parseInt(getVal(backClicksIdx)) || 0,
      scrollDepth: getVal(scrollIdx),
      screenTimes,
      lastScreenIndex: parseInt(getVal(lastScreenIdx)) || 0,
      completed: getVal(completedIdx).toLowerCase() === 'ja',
    };
  });
}

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [pageviews, setPageviews] = useState<{ created_at: string; device_type: string | null }[]>([]);
  const [livePageviews, setLivePageviews] = useState(0);
  const [liveStarted, setLiveStarted] = useState(0);
  const [liveCompleted, setLiveCompleted] = useState(0);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(startOfDay(new Date()));
  const [dateFromAutoSet, setDateFromAutoSet] = useState(true);
  const [dateTo, setDateTo] = useState<Date | undefined>(startOfDay(new Date()));
  const [pendingFrom, setPendingFrom] = useState<Date | undefined>(startOfDay(new Date()));
  const [pendingTo, setPendingTo] = useState<Date | undefined>(startOfDay(new Date()));
  const [hasPendingChange, setHasPendingChange] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'answers' | 'utm' | 'data' | 'abtest' | 'ads'>('overview');

  // Parse DD-MM-YYYY HH:mm:ss to Date
  const parseDutchDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    try {
      const [datePart, timePart] = dateStr.split(' ');
      const [day, month, year] = datePart.split('-').map(Number);
      if (timePart) {
        const [h, m, s] = timePart.split(':').map(Number);
        return new Date(year, month - 1, day, h, m, s);
      }
      return new Date(year, month - 1, day);
    } catch { return null; }
  };

  const fetchLiveCounts = async () => {
    const fromIso = dateFrom ? format(startOfDay(dateFrom), "yyyy-MM-dd'T'HH:mm:ss") : null;
    let toIso: string | null = null;
    if (dateTo) {
      const endDate = new Date(dateTo);
      endDate.setDate(endDate.getDate() + 1);
      toIso = format(startOfDay(endDate), "yyyy-MM-dd'T'HH:mm:ss");
    }

    // Pageviews count goes through the secured edge function (RLS no longer permits anon SELECT on quiz_pageviews).
    const pvFilters: { column: string; operator: string; value: unknown }[] = [
      { column: 'utm_source', operator: 'not.is', value: null },
    ];
    if (fromIso) pvFilters.push({ column: 'created_at', operator: 'gte', value: fromIso });
    if (toIso) pvFilters.push({ column: 'created_at', operator: 'lt', value: toIso });

    const buildStarted = () => {
      let q = supabase.from('quiz_sessions').select('*', { count: 'exact', head: true })
        .not('utm_source', 'is', null)
        .gte('current_step', 2);
      if (fromIso) q = q.gte('created_at', fromIso);
      if (toIso) q = q.lt('created_at', toIso);
      return q;
    };
    const buildCompleted = () => {
      let q = supabase.from('quiz_sessions').select('*', { count: 'exact', head: true })
        .not('utm_source', 'is', null)
        .eq('is_completed', true);
      if (fromIso) q = q.gte('created_at', fromIso);
      if (toIso) q = q.lt('created_at', toIso);
      return q;
    };

    const [pvCount, started, completed] = await Promise.all([
      dashboardCount('quiz_pageviews', pvFilters),
      buildStarted(),
      buildCompleted(),
    ]);
    setLivePageviews(pvCount);
    setLiveStarted(started.count ?? 0);
    setLiveCompleted(completed.count ?? 0);
  };

  const fetchData = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const key = urlParams.get('key') || '';
      const fromIso = dateFrom ? format(startOfDay(dateFrom), "yyyy-MM-dd'T'HH:mm:ss") : null;
      let toIso: string | null = null;
      if (dateTo) {
        const endDate = new Date(dateTo);
        endDate.setDate(endDate.getDate() + 1);
        toIso = format(startOfDay(endDate), "yyyy-MM-dd'T'HH:mm:ss");
      }
      const pvFilters: { column: string; operator: string; value: unknown }[] = [
        { column: 'utm_source', operator: 'not.is', value: null },
      ];
      if (fromIso) pvFilters.push({ column: 'created_at', operator: 'gte', value: fromIso });
      if (toIso) pvFilters.push({ column: 'created_at', operator: 'lt', value: toIso });
      const [sheetResult, pvRows] = await Promise.all([
        supabase.functions.invoke('read-google-sheets', { body: { key } }).catch(() => ({ data: null, error: 'Sheet fetch failed' })),
        fetchAllRows<{ created_at: string; device_type: string | null }>('quiz_pageviews', 'created_at, device_type', pvFilters),
      ]);
      if (sheetResult.data && !sheetResult.error) {
        const newHeaders = sheetResult.data.headers || [];
        const newRows = sheetResult.data.rows || [];
        if (newHeaders.length > 0 || rawRows.length === 0) {
          setHeaders(newHeaders);
          setRawRows(newRows);
        }
      }
      setPageviews(pvRows || []);
    } catch (err: any) {
      if (rawRows.length === 0 && pageviews.length === 0) {
        setError(err.message || 'Kon data niet laden');
      }
    } finally {
      setLoading(false);
    }
    await fetchLiveCounts();
  };

  const liveCountsTimerRef = useRef<number | null>(null);
  const scheduleLiveCounts = () => {
    if (liveCountsTimerRef.current !== null) return;
    liveCountsTimerRef.current = window.setTimeout(() => {
      liveCountsTimerRef.current = null;
      fetchLiveCounts();
    }, 3000);
  };

  useEffect(() => {
    fetchData();
    // Slower polling fallback for Google Sheets data (5 min — realtime handles
    // immediate updates, polling is only a safety net)
    const interval = setInterval(fetchData, 5 * 60 * 1000);

    // Subscribe to realtime changes — debounce burst updates so a wave of
    // session heartbeats doesn't trigger one count query per event.
    const channel = supabase
      .channel('dashboard-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_sessions' }, scheduleLiveCounts)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_pageviews' }, scheduleLiveCounts)
      .subscribe();

    return () => {
      clearInterval(interval);
      if (liveCountsTimerRef.current !== null) clearTimeout(liveCountsTimerRef.current);
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch live counts when date filters change
  useEffect(() => {
    fetchLiveCounts();
  }, [dateFrom, dateTo]);

  // Auto-set dateFrom to earliest pageview so sheet data aligns with pageview tracking
  useEffect(() => {
    if (!dateFromAutoSet && pageviews.length > 0) {
      const earliest = pageviews.reduce((min, pv) => {
        const d = new Date(pv.created_at);
        return d < min ? d : min;
      }, new Date(pageviews[0].created_at));
      setDateFrom(startOfDay(earliest));
      setDateFromAutoSet(true);
    }
  }, [pageviews, dateFromAutoSet]);

  const filteredPageviews = useMemo(() => {
    return pageviews.filter(pv => {
      try {
        const d = new Date(pv.created_at);
        if (dateFrom && isBefore(d, startOfDay(dateFrom))) return false;
        if (dateTo && isAfter(d, endOfDay(dateTo))) return false;
        return true;
      } catch { return true; }
    });
  }, [pageviews, dateFrom, dateTo]);

  const rows = useMemo(() => {
    const all = parseRows(headers, rawRows);
    return all.filter(r => {
      if (!r.date) return true;
      const d = parseDutchDate(r.date);
      if (!d) return true;
      if (dateFrom && isBefore(d, startOfDay(dateFrom))) return false;
      if (dateTo && isAfter(d, endOfDay(dateTo))) return false;
      return true;
    });
  }, [headers, rawRows, dateFrom, dateTo]);

  const filteredRawRows = useMemo(() => {
    const datumIdx = headers.indexOf('Datum');
    return rawRows.filter(row => {
      const dateStr = datumIdx >= 0 ? row[datumIdx] : row[0];
      if (!dateStr) return true;
      const d = parseDutchDate(dateStr);
      if (!d) return true;
      if (dateFrom && isBefore(d, startOfDay(dateFrom))) return false;
      if (dateTo && isAfter(d, endOfDay(dateTo))) return false;
      return true;
    });
  }, [rawRows, headers, dateFrom, dateTo]);

  const stats = useMemo(() => {
    if (rows.length === 0 && filteredPageviews.length === 0) return null;

    const totalPageviews = filteredPageviews.length;
    const completed = rows.filter(r => r.completed);
    const cvr = totalPageviews > 0 ? Math.round((completed.length / totalPageviews) * 100) : 0;

    const durations = rows.map(r => parseInt(r.totalDuration) || 0).filter(d => d > 0);
    const avgDuration = durations.length > 0 ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;

    const totalBackClicks = rows.reduce((sum, r) => sum + r.backClicks, 0);
    const avgBackClicks = rows.length > 0 ? (totalBackClicks / rows.length).toFixed(1) : '0';

    // Device breakdown
    const deviceCounts: Record<string, number> = {};
    rows.forEach(r => {
      const d = r.deviceType || 'onbekend';
      deviceCounts[d] = (deviceCounts[d] || 0) + 1;
    });
    const deviceData = Object.entries(deviceCounts).map(([name, value]) => ({ name, value }));

    // Drop-off per screen
    const screenCounts: Record<number, number> = {};
    rows.forEach(r => {
      for (let i = 0; i <= r.lastScreenIndex; i++) {
        screenCounts[i] = (screenCounts[i] || 0) + 1;
      }
    });
    const maxScreen = Math.max(...Object.keys(screenCounts).map(Number), 0);
    const dropoffData = [];
    for (let i = 0; i <= maxScreen; i++) {
      const visitors = screenCounts[i] || 0;
      const prevVisitors = i === 0 ? rows.length : (screenCounts[i - 1] || 0);
      const dropoffPct = prevVisitors > 0 ? Math.round(((prevVisitors - visitors) / prevVisitors) * 100) : 0;
      dropoffData.push({ name: `Scherm ${i + 1}`, bezoekers: visitors, afhaakPct: dropoffPct });
    }

    // Answer distribution per question
    const skipHeaders = new Set(['Sessie ID', 'Datum', 'Voltooid', ...METRIC_HEADERS]);
    const answerKeys = headers.filter(h => !skipHeaders.has(h));
    const answerDistributions = answerKeys.map(question => {
      const counts: Record<string, number> = {};
      rows.forEach(r => {
        const val = r.answers[question];
        if (val) counts[val] = (counts[val] || 0) + 1;
      });
      return {
        question,
        data: Object.entries(counts)
          .map(([name, value]) => ({ name: name.length > 25 ? name.substring(0, 25) + '…' : name, value, fullName: name }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10),
      };
    }).filter(d => d.data.length > 0);

    // Daily submissions
    const dailyCounts: Record<string, number> = {};
    rows.forEach(r => {
      const day = r.date.substring(0, 10);
      if (day) dailyCounts[day] = (dailyCounts[day] || 0) + 1;
    });
    const dailyData = Object.entries(dailyCounts)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Average screen times
    const screenTimeTotals: Record<string, { total: number; count: number }> = {};
    rows.forEach(r => {
      Object.entries(r.screenTimes).forEach(([screen, time]) => {
        if (!screenTimeTotals[screen]) screenTimeTotals[screen] = { total: 0, count: 0 };
        screenTimeTotals[screen].total += time;
        screenTimeTotals[screen].count++;
      });
    });
    const screenTimeData = Object.entries(screenTimeTotals)
      .map(([name, { total, count }]) => ({
        name: name.replace('screen_', 'Scherm ').replace(/(\d+)/, (_, n) => String(Number(n) + 1)),
        seconds: Math.round(total / count),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      total: rows.length,
      totalPageviews,
      completed: completed.length,
      cvr,
      avgDuration,
      avgBackClicks,
      durations,
      deviceData,
      dropoffData,
      answerDistributions,
      dailyData,
      screenTimeData,
    };
  }, [rows, headers, filteredPageviews]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-400 text-lg">Dashboard laden...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <p className="text-red-500 text-lg font-medium mb-2">Fout bij laden</p>
          <p className="text-gray-500">{error}</p>
          <button onClick={() => navigate('/')} className="mt-4 text-blue-600 underline">Terug naar quiz</button>
        </div>
      </div>
    );
  }

  if (!stats && livePageviews === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <p className="text-gray-500 text-lg">Nog geen data beschikbaar</p>
          <button onClick={() => navigate('/')} className="mt-4 text-blue-600 underline">Terug naar quiz</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'overview' as const, label: 'Overzicht', icon: Eye },
    // Meta Ads tab removed
    { key: 'abtest' as const, label: 'A/B Test', icon: FlaskConical },
    { key: 'answers' as const, label: 'Antwoorden', icon: BarChart3 },
    { key: 'utm' as const, label: 'UTM & Bronnen', icon: TrendingUp },
    { key: 'data' as const, label: 'Ruwe Data', icon: Monitor },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Quiz Dashboard — Adelira</title>
        <meta name="description" content="Interne analytics voor de Adelira quiz funnel." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://quiz.adelira.nl/dashboard" />
      </Helmet>
      {/* Header */}
      <div className="bg-[#0F1C2E] text-white px-4 sm:px-8 py-5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4 flex-1">
            <button onClick={() => navigate('/')} className="p-2 hover:bg-white/10 rounded-lg transition">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Adelira Quiz Dashboard</h1>
              <p className="text-sm text-white/60">{livePageviews} pageviews • {liveStarted} gestart • {liveCompleted} voltooid</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap",
                activeTab === tab.key
                  ? "border-[#0F1C2E] text-[#0F1C2E]"
                  : "border-transparent text-gray-400 hover:text-gray-600"
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 space-y-6">
        {/* ========== OVERVIEW TAB ========== */}
        {activeTab === 'overview' && (
          <>
            <DateFilterBar
              pendingFrom={pendingFrom}
              pendingTo={pendingTo}
              hasPendingChange={hasPendingChange}
              onSelectFrom={(d) => { setPendingFrom(d); setHasPendingChange(true); }}
              onSelectTo={(d) => { setPendingTo(d); setHasPendingChange(true); }}
              onApply={() => { setDateFrom(pendingFrom); setDateTo(pendingTo); setHasPendingChange(false); setDateFromAutoSet(true); }}
              onReset={() => { setPendingFrom(undefined); setPendingTo(undefined); setDateFrom(undefined); setDateTo(undefined); setHasPendingChange(false); }}
              onPresetApply={(from, to) => { setDateFrom(from); setDateTo(to); setHasPendingChange(false); setDateFromAutoSet(true); }}
            />

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard icon={Eye} label="Pageviews" value={String(livePageviews)} sub={`${liveStarted} gestart`} />
              <StatCard icon={TrendingUp} label="CVR (Pageview → Voltooid)" value={`${livePageviews > 0 ? Math.round((liveCompleted / livePageviews) * 100) : 0}%`} sub={`${liveCompleted} van ${livePageviews} pageviews`} />
              <StatCard icon={Clock} label="Gem. doorlooptijd" value={`${stats?.avgDuration || 0}s`} sub={`${Math.floor((stats?.avgDuration || 0) / 60)}m ${(stats?.avgDuration || 0) % 60}s`} />
              <StatCard icon={MousePointerClick} label="Gem. terug-kliks" value={stats?.avgBackClicks || '0'} />
              <StatCard icon={Users} label="Afgehaakt" value={String(Math.max(0, liveStarted - liveCompleted))} sub={`${liveStarted > 0 ? Math.round(((liveStarted - liveCompleted) / liveStarted) * 100) : 0}% drop-off`} />
            </div>


            {/* Live Activity + Funnel */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <LiveActivityFeed />
              <ConversionFunnel pageviews={livePageviews} started={liveStarted} completed={liveCompleted} />
            </div>




            {/* Afhaak Funnel - LIVE */}
            <LiveAfhaakFunnel />

            {/* Timing + Device Conversion */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <LiveTimingMetrics />
              <LiveDeviceConversion />
            </div>

            {/* UTM + Hour Heatmap */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <LiveUTMPerformance />
              <LiveHourHeatmap />
            </div>

            {/* Logo-kliks tracking */}
            <LiveLogoClicks />

            {/* Bounce & Conversie uitsplitsing per apparaat en UTM */}
            <LiveBounceConversionSplit />

            {/* Dagelijkse inzendingen */}
            {stats && stats.dailyData.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  Dagelijkse inzendingen
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={stats.dailyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#0F1C2E" strokeWidth={2} dot={{ r: 3 }} name="Inzendingen" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Gem. schermtijden (Google Sheets data) */}
            {stats && stats.screenTimeData.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Gem. schermtijden (Sheets)
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={stats.screenTimeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="s" />
                    <Tooltip formatter={(v: number) => [`${v}s`, 'Gem. tijd']} />
                    <Bar dataKey="seconds" fill="#0F1C2E" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Drop-off per scherm (Google Sheets data) */}
            {stats && stats.dropoffData.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Drop-off per scherm (Sheets)
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.dropoffData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="bezoekers" fill="#0F1C2E" radius={[4,4,0,0]} name="Bezoekers" />
                    <Bar dataKey="afhaakPct" fill="#ef4444" radius={[4,4,0,0]} name="Afhaak %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Apparaat-verdeling pie chart (Sheets) */}
            {stats && stats.deviceData.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Monitor className="w-4 h-4" />
                  Apparaat-verdeling (Sheets)
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={stats.deviceData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {stats.deviceData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}


        {/* ========== ANSWERS TAB ========== */}
        {activeTab === 'answers' && (
          <LiveAnswerDistribution />
        )}

        {/* ========== A/B TEST TAB ========== */}
        {activeTab === 'abtest' && (
          <div className="space-y-6">
            <LiveABTest />
            <ABSpendSplit />
            <LiveShopifyOrders />
            <ABVisualComparison />
            <ABTestLogbook />
          </div>
        )}


        {/* ========== UTM TAB ========== */}
        {activeTab === 'utm' && (
          <LiveUTMPerformance />
        )}

        {/* ========== RAW DATA TAB ========== */}
        {activeTab === 'data' && (
          <LiveRawData dateFrom={dateFrom} dateTo={dateTo} />
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
